import { useState } from 'react';
import { SubscriptionPlan } from '../../types/plan';
import { PlanSelectionCard } from './PlanSelectionCard';
import { useTranslation } from '../../hooks/useTranslation';

interface PlanComparisonGridProps {
  plans: SubscriptionPlan[];
  currentPlan?: string;
  mode: 'selection' | 'comparison' | 'upgrade';
  onSelectPlan: (planCode: string) => void;
  selectedPlan?: string;
}

export const PlanComparisonGrid = ({
  plans,
  currentPlan,
  mode,
  onSelectPlan,
  selectedPlan,
}: PlanComparisonGridProps) => {
  const { t } = useTranslation();
  const [internalSelectedPlan, setInternalSelectedPlan] = useState<string>(
    selectedPlan || currentPlan || ''
  );

  const handleSelectPlan = (planCode: string) => {
    setInternalSelectedPlan(planCode);
    onSelectPlan(planCode);
  };

  const getTitle = () => {
    switch (mode) {
      case 'selection':
        return t('plans.selectPlan');
      case 'comparison':
        return t('plans.comparePlans');
      case 'upgrade':
        return t('plans.upgradePlan');
      default:
        return t('plans.title');
    }
  };

  // Sort plans by display order
  const sortedPlans = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);

  // Determine which plan to highlight as recommended
  const getRecommendedPlan = () => {
    if (mode === 'upgrade' && currentPlan) {
      // Recommend the next tier up
      const currentIndex = sortedPlans.findIndex(p => p.planCode === currentPlan);
      if (currentIndex >= 0 && currentIndex < sortedPlans.length - 1) {
        return sortedPlans[currentIndex + 1].planCode;
      }
    }
    // Default to ONE plan as recommended for new users
    return 'ONE';
  };

  const recommendedPlan = getRecommendedPlan();

  return (
    <div className="w-full">
      {/* Title */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{getTitle()}</h2>
        {mode === 'upgrade' && currentPlan && (
          <p className="text-gray-600">
            {t('plans.currentPlan')}: {sortedPlans.find(p => p.planCode === currentPlan)?.name || currentPlan}
          </p>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {sortedPlans.map((plan) => (
          <PlanSelectionCard
            key={plan.planCode}
            plan={plan}
            isSelected={internalSelectedPlan === plan.planCode}
            onSelectPlan={handleSelectPlan}
            showCTA={mode !== 'comparison'}
            highlightRecommended={plan.planCode === recommendedPlan}
            isCurrentPlan={mode === 'upgrade' && currentPlan === plan.planCode}
          />
        ))}
      </div>
    </div>
  );
};
